/** Per-user column visibility + order for RCM Process / ITGC / All tabs. */
export type RcmColumnScope = 'process' | 'itgc' | 'all';

const STORAGE_VERSION = 'v1';

function storageKey(scope: RcmColumnScope, kind: 'visibility' | 'order'): string {
  const base = `sox-rcm:${scope}:columns-${kind}-${STORAGE_VERSION}`;
  if (typeof window === 'undefined') return base;
  const userId = window.localStorage.getItem('userId');
  return userId ? `${base}:${userId}` : base;
}

export function readStoredRcmColumnVisibility(
  scope: RcmColumnScope,
  createDefault: () => Record<string, boolean>,
  knownColumnIds: string[],
): Record<string, boolean> {
  const defaults = createDefault();
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(storageKey(scope, 'visibility'));
    if (raw == null || raw === '') return defaults;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;
    const known = new Set(knownColumnIds);
    const out = { ...defaults };
    for (const [id, visible] of Object.entries(parsed as Record<string, unknown>)) {
      if (known.has(id) && typeof visible === 'boolean') {
        out[id] = visible;
      }
    }
    return out;
  } catch {
    return defaults;
  }
}

export function readStoredRcmColumnOrder(
  scope: RcmColumnScope,
  getDefaultOrder: () => string[],
  knownColumnIds: string[],
): string[] {
  const defaults = getDefaultOrder();
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(storageKey(scope, 'order'));
    if (raw == null || raw === '') return defaults;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaults;
    const known = new Set(knownColumnIds);
    const filtered = parsed.filter((id): id is string => typeof id === 'string' && known.has(id));
    const missing = defaults.filter((id) => !filtered.includes(id));
    return [...filtered, ...missing];
  } catch {
    return defaults;
  }
}

export function writeStoredRcmColumnVisibility(
  scope: RcmColumnScope,
  visibility: Record<string, boolean>,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(scope, 'visibility'), JSON.stringify(visibility));
  } catch {
    /* ignore quota / private mode */
  }
}

export function writeStoredRcmColumnOrder(scope: RcmColumnScope, order: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(scope, 'order'), JSON.stringify(order));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStoredRcmColumnPreferences(scope: RcmColumnScope): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(scope, 'visibility'));
    window.localStorage.removeItem(storageKey(scope, 'order'));
  } catch {
    /* ignore */
  }
}
